import React, { FC, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import ColoredTile from 'components/ColoredTile';
import MobileGuttersContainer from 'components/MobileGuttersContainer';
import Section from 'components/Section';
import TextButton from 'components/TextButton/TextButton';
import manage_dashboard_messages from 'messages/manage_dasboard_messages';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import { Id } from 'types/Id';

import useGetManagementReportBaseData from '../../_hooks/useGetManagementReportBaseData';
import ManagementCommentInput from '../ManagementCommentInput/ManagementCommentInput';
import ManagementDashboardKPIGrid from '../ManagementDashboardKPIGrid/ManagementDashboardKPIGrid';
import ManagementDashboardSectionGrid from '../ManagementDashboardSectionGrid/ManagementDashboardSectionGrid';

type Props = {
  reportId?: Id;
};

const ManagementDashboardDsarSection: FC<Props> = ({ reportId }) => {
  const { t } = useTranslation();
  const { showHelpDialog } = useHelpDialogContext();

  const showIntroductionDialog = () => {
    showHelpDialog({
      title: t(manage_dashboard_messages.dsar_section.introduction_title),
      body: t(manage_dashboard_messages.dsar_section.introduction_body),
    });
  };

  const { data } = useGetManagementReportBaseData(reportId);

  const kpiData = useMemo(() => {
    const { dsarReport } = data || {};
    return [
      {
        description: t(manage_dashboard_messages.dsar_section.received_cases_kpi_label),
        title: dsarReport?.receivedCases ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.closed_cases_kpi_label),
        title: dsarReport?.closedCases ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.closed_within_30_days_kpi_label),
        title: dsarReport?.closedWithin30Days ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.closed_after_30_days_kpi_label),
        title: dsarReport?.closedAfter30Days ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.extended_cases_kpi_label),
        title: dsarReport?.extendedCases ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.third_party_risk_cases_kpi_label),
        title: dsarReport?.thirdPartyRiskCases ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.manual_review_cases_kpi_label),
        title: dsarReport?.manualReviewCases ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.open_cases_at_period_end_kpi_label),
        title: dsarReport?.openCasesAtPeriodEnd ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.open_cases_older_than_30_days_kpi_label),
        title: dsarReport?.openCasesOlderThan30Days ?? null,
      },
      {
        description: t(manage_dashboard_messages.dsar_section.average_handling_time_kpi_label),
        title: dsarReport ? dsarReport.averageHandlingTimeDays.toFixed(1) : null,
      },
    ];
  }, [data, t]);

  return (
    <Section
      title={manage_dashboard_messages.dsar_section.title}
      titleLineAddon={[
        <TextButton key='introduction' onClick={showIntroductionDialog}>
          {t(manage_dashboard_messages.dsar_section.introduction_button)}
        </TextButton>,
      ]}
      // @ts-ignore
      titleWrapper={MobileGuttersContainer}
    >
      <ManagementDashboardSectionGrid>
        <MobileGuttersContainer>
          <ManagementDashboardKPIGrid>
            {kpiData.map(({ description, title }) => (
              <ColoredTile key={description} centered description={description} disableRipple isLoading={title === null} title={title} />
            ))}
          </ManagementDashboardKPIGrid>
          <ManagementCommentInput fieldKey='dsarReport' initialValue={data?.dsarReport.comment || ''} reportId={reportId} />
        </MobileGuttersContainer>
      </ManagementDashboardSectionGrid>
    </Section>
  );
};

export default ManagementDashboardDsarSection;
